import { ref } from 'vue';

type Cache = {
  timestamp: number;
  response: unknown;
  func: (...args: unknown[]) => Promise<unknown>;
};

const caches = ref<Record<string, Cache>>({});
const MIN_5 = 1 * 60 * 1000;

const toCacheKey = <F extends (...args: unknown[]) => Promise<unknown>>(func: F, args: Parameters<F>) =>
  JSON.stringify({
    args,
    f: func.toString(),
  });

const cleanOldCaches = () => {
  Object.keys(caches.value).forEach((key) => {
    if (caches.value[key].timestamp + MIN_5 < Date.now()) {
      delete caches.value[key];
    }
  });
};

export const memoizeFunction = async <F extends (...args: any[]) => Promise<any>>(func: F, ...args: Parameters<F>) => {
  cleanOldCaches();
  const cacheKey = toCacheKey(func, args);
  const cache = caches.value[cacheKey];

  if (!cache || cache.timestamp + MIN_5 < Date.now()) {
    const response = await func(...args);
    caches.value[cacheKey] = {
      timestamp: Date.now(),
      func,
      response,
    };
  }

  return caches.value[cacheKey].response as ReturnType<typeof func>;
};
